import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { TrainingPartner, Training } from '@/types/trainings';
import { useToast } from '@/hooks/use-toast';

interface UseTrainingPartnersProps {
  training?: Training | null;
  trainingId?: string;
}

export const useTrainingPartners = (props: UseTrainingPartnersProps | null) => {
  const { training, trainingId } = props || {};
  const { user } = useAuth();
  const { toast } = useToast();
  const [partners, setPartners] = useState<TrainingPartner[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Get the actual training ID
  const actualTrainingId = training?.id || trainingId;

  // Load partners when training changes
  useEffect(() => {
    const fetchPartners = async () => {
      if (!actualTrainingId || !user) {
        setPartners([]);
        return;
      }
      
      setLoading(true);
      try {
        const { data: { session } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getSession());
        const token = session?.access_token;

        // Use local Supabase functions URL in development
        const baseUrl = import.meta.env.DEV
          ? 'http://127.0.0.1:54321'
          : import.meta.env.VITE_SUPABASE_URL;

        const response = await fetch(`${baseUrl}/functions/v1/get_training_partners`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ training_id: actualTrainingId })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('🔄 useTrainingPartners - Response error:', errorText);
          throw new Error('Failed to fetch partners');
        }
        
        const result = await response.json();
        setPartners(result.data || []);
      } catch (error) {
        console.error('🔄 useTrainingPartners - Error fetching partners:', error);
        // Only show toast if we don't have fallback data
        // The TrainingViewModal handles its own fallback, so we don't need to show error here
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, [actualTrainingId, user, toast]);

  // Load partners from training data (this will be populated from the training object)
  const loadPartners = useCallback((trainingPartners: TrainingPartner[] = []) => {
    setPartners(trainingPartners);
  }, []);

  // Add partner to training
  const addPartner = useCallback(async (partner: TrainingPartner) => {
    if (!user || !actualTrainingId) {
      return;
    }

    setSaving(true);
    try {
      // Get the session token
      const { data: { session } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getSession());
      const token = session?.access_token;

      // Use local Supabase functions URL in development
      const baseUrl = import.meta.env.DEV
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(`${baseUrl}/functions/v1/link_training_partners`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          trainingId: actualTrainingId,
          action: 'add',
          partnerData: {
            partner_user_id: partner.partner_user_id,
            manual_partner_name: partner.manual_partner_name
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔄 useTrainingPartners - Response error:', errorText);
        throw new Error('Failed to add partner');
      }

      const result = await response.json();
      setPartners(prev => [...prev, { ...partner, id: result.data.id }]);

      toast({
        title: "Partner added",
        description: "Training partner added successfully",
      });

      return result.data;
    } catch (error) {
      console.error('🔄 useTrainingPartners - Error adding partner:', error);
      toast({
        title: "Error",
        description: "Failed to add training partner",
        variant: "destructive",
      });
      throw error;
    } finally {
      setSaving(false);
    }
  }, [user, actualTrainingId, toast]);

  // Remove partner from training
  const removePartner = useCallback(async (partnerId: string) => {
    setSaving(true);
    try {
      // Get the session token
      const { data: { session } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getSession());
      const token = session?.access_token;

      if (!token) {
        throw new Error('No access token');
      }

      // Use local Supabase functions URL in development
      const baseUrl = import.meta.env.DEV
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      const requestBody = {
        trainingId: actualTrainingId,
        action: 'remove',
        partnerData: {
          partner_id: partnerId
        }
      };

      // Remove the specific partner from database
      const response = await fetch(`${baseUrl}/functions/v1/link_training_partners`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error removing partner:', errorText);
        throw new Error(`Failed to remove partner: ${errorText}`);
      }

      const result = await response.json();

      // Update local state
      setPartners(prev => {
        const newPartners = prev.filter(p => p.id !== partnerId);
        return newPartners;
      });
    } catch (error) {
      console.error('Error removing partner:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [actualTrainingId]);

  // Save all partners at once (for batch operations)
  const savePartners = useCallback(async (partnersToSave: TrainingPartner[]) => {
    if (!user || !actualTrainingId) return;

    setSaving(true);
    try {
      // First, remove all existing partners
      for (const partner of partners) {
        await removePartner(partner.id);
      }

      // Then add all new partners
      for (const partner of partnersToSave) {
        await addPartner(partner);
      }

      toast({
        title: "Partners saved",
        description: "Training partners updated successfully",
      });
    } catch (error) {
      console.error('Error saving partners:', error);
      toast({
        title: "Error",
        description: "Failed to save training partners",
        variant: "destructive",
      });
      throw error;
    } finally {
      setSaving(false);
    }
  }, [user, actualTrainingId, partners, removePartner, addPartner, toast]);

  return {
    partners,
    loading,
    saving,
    loadPartners,
    addPartner,
    removePartner,
    savePartners,
  };
}; 

// Hook for using pre-loaded partners data
export const useTrainingPartnersFromData = (trainingId: string, partnersData?: any[], refreshKey?: number) => {
  const [partners, setPartners] = useState<TrainingPartner[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  // Load partners from API if no data is provided
  const loadPartnersFromAPI = useCallback(async () => {
    if (!trainingId) return;
    
    setLoading(true);
    try {
      const { data: { session } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getSession());
      const token = session?.access_token;

      if (!token) {
        console.error('No access token available');
        return;
      }

      const baseUrl = import.meta.env.DEV
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(`${baseUrl}/functions/v1/get_training_partners?t=${Date.now()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ training_id: trainingId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔄 useTrainingPartnersFromData - Error response:', errorText);
        return;
      }

      const result = await response.json();
      setPartners(result.data || []);
    } catch (error) {
      console.error('🔄 useTrainingPartnersFromData - Error loading partners from API:', error);
    } finally {
      setLoading(false);
    }
  }, [trainingId]);

  // Initialize partners from data or load from API
  useEffect(() => {
    if (partnersData && Array.isArray(partnersData) && partnersData.length > 0) {
      // Use provided data
      const dataString = JSON.stringify(partnersData);
      const partnersString = JSON.stringify(partners);
      if (dataString !== partnersString) {
        setPartners(partnersData);
      }
      setLoading(false);
    } else {
      // Load from API if no data provided
      loadPartnersFromAPI();
    }
  }, [trainingId, JSON.stringify(partnersData), loadPartnersFromAPI]);

  // Reload partners when refreshKey changes
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      // Force a small delay to ensure any pending operations are completed
      setTimeout(() => {
        loadPartnersFromAPI();
      }, 100);
    }
  }, [refreshKey, loadPartnersFromAPI]);

  // Load partners from training data (this will be populated from the training object)
  const loadPartners = useCallback((trainingPartners: TrainingPartner[] = []) => {
    setPartners(trainingPartners);
    setHasLocalChanges(false);
  }, []);

  // Add partner to training (still needs individual request for updates)
  const addPartner = useCallback(async (partner: TrainingPartner) => {
    setPartners(prev => [...prev, partner]);
    setHasLocalChanges(true);
  }, []);

  // Remove partner from training
  const removePartner = useCallback(async (partnerId: string) => {
    setSaving(true);
    try {
      // Get the session token
      const { data: { session } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getSession());
      const token = session?.access_token;

      if (!token) {
        throw new Error('No access token');
      }

      // Use local Supabase functions URL in development
      const baseUrl = import.meta.env.DEV
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      const requestBody = {
        trainingId: trainingId,
        action: 'remove',
        partnerData: {
          partner_id: partnerId
        }
      };
      
      // Remove the specific partner from database
      const response = await fetch(`${baseUrl}/functions/v1/link_training_partners`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error removing partner:', errorText);
        throw new Error(`Failed to remove partner: ${errorText}`);
      }

      const result = await response.json();

      // Update local state
      setPartners(prev => {
        const newPartners = prev.filter(p => p.id !== partnerId);
        return newPartners;
      });
    } catch (error) {
      console.error('Error removing partner:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [trainingId]);

  // Save partners (for backward compatibility)
  const savePartners = useCallback(async (partnersToSave: TrainingPartner[]) => {
    if (!partnersToSave || partnersToSave.length === 0) {
      setPartners([]);
      setHasLocalChanges(false);
      return;
    }

    setSaving(true);
    try {
      // Get the session token
      const { data: { session } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getSession());
      const token = session?.access_token;

      if (!token) {
        throw new Error('No access token');
      }

      // Use local Supabase functions URL in development
      const baseUrl = import.meta.env.DEV
        ? 'http://127.0.0.1:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      // First, remove all existing partners
      for (const partner of partners) {
        if (partner.id) {
          const response = await fetch(`${baseUrl}/functions/v1/link_training_partners`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              trainingId: trainingId,
              action: 'remove',
              partnerData: {
                partner_id: partner.id
              }
            })
          });

          if (!response.ok) {
            console.error('Error removing partner:', await response.text());
          }
        }
      }

      // Then add all new partners
      for (const partner of partnersToSave) {
        // Check if partner already exists in the new list (partnersToSave)
        const partnerExistsInNewList = partnersToSave.filter(p => 
          (p.partner_user_id && p.partner_user_id === partner.partner_user_id) ||
          (p.manual_partner_name && p.manual_partner_name === partner.manual_partner_name)
        ).length > 1;
        
        if (partnerExistsInNewList) {
          console.log('Partner already exists in new list, skipping:', partner);
          continue;
        }
        
        const response = await fetch(`${baseUrl}/functions/v1/link_training_partners`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            trainingId: trainingId,
            action: 'add',
            partnerData: {
              partner_user_id: partner.partner_user_id,
              manual_partner_name: partner.manual_partner_name
            }
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error adding partner:', errorText);
          
          // If it's a 409 conflict, it means the partner already exists in database, so we can ignore this error
          if (response.status === 409) {
            console.log('Partner already exists in database, continuing...');
          } else {
            throw new Error(`Failed to add partner: ${errorText}`);
          }
        }
      }

      setPartners(partnersToSave);
      setHasLocalChanges(false);
    } catch (error) {
      console.error('Error saving partners:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [trainingId, partners]);

  return {
    partners,
    loading,
    saving,
    addPartner,
    removePartner,
    savePartners,
    loadPartners
  };
}; 